/**
 * Formatierung und feste Reihenfolgen der Vertriebsauswertung. Zahlenformate
 * (Prozent, Vorzeichen, Farben) kommen bewusst aus der Verkaufsstatistik
 * (Components/Statistics/format.js) - eine Formatierungslogik im ganzen
 * Haus, keine zweite Fassung.
 */
export {
    formatPercent,
    formatSignedPercent,
    formatNumber,
    formatQuantity,
    formatDateDE,
    TREND_UP_COLOR,
    TREND_DOWN_COLOR,
    TREND_NEUTRAL_COLOR,
} from "../Statistics/format.js";

const euroWholeFormatter = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

/**
 * Euro ohne Nachkommastellen - für die großen Jahres-/Kundentabellen der
 * Vertriebsauswertung (Vorgabe: "Euro ohne Nachkommastellen in großen
 * Tabellen"). Für Einzelbeträge (z. B. Ladenhüter-Schwelle) bleibt die
 * zweistellige Fassung aus Components/Statistics/format.js zuständig.
 */
export function formatEuroWhole(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "–";
    }
    return euroWholeFormatter.format(value);
}

// Reihenfolge wie in der Excel-Datei (fortschritt.md, Abschnitt "Sichten und
// Spalten"): Marker Systems, Spray Paint, OEM, Andere. Unbekannte oder neue
// Artikelarten landen ans Ende, statt zu verschwinden.
export const ARTICLE_TYPE_ORDER = ["Marker Systems", "Spray Paint", "OEM", "Andere"];

export function sortByArticleTypeOrder(rows, key = "article_type") {
    return [...(rows || [])].sort((a, b) => {
        const ai = ARTICLE_TYPE_ORDER.indexOf(a[key]);
        const bi = ARTICLE_TYPE_ORDER.indexOf(b[key]);
        return (ai < 0 ? ARTICLE_TYPE_ORDER.length : ai) - (bi < 0 ? ARTICLE_TYPE_ORDER.length : bi);
    });
}

export const MONTH_NAMES = [
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export function monthLabel(month) {
    const index = Number(month) - 1;
    return MONTH_NAMES[index] || String(month);
}

/**
 * Ermittelt die anzuzeigenden Jahresspalten aus dem tatsächlich gelieferten
 * "years"-Objekt einer Zeile bzw. Summenzeile - nie hart verdrahtet, wie von
 * der Aufgabe gefordert. Das laufende Jahr (meta.current_year) wird separat
 * ausgewiesen, weil es nur bis zum Stichmonat befüllt ist ("2026 bis
 * August" in der Excel-Vorlage) und die Hochrechnung eine eigene Spalte
 * bekommt.
 */
export function pastYearColumns(years, currentYear) {
    return Object.keys(years || {})
        .filter((year) => Number(year) !== Number(currentYear))
        .sort((a, b) => Number(a) - Number(b));
}

/**
 * Wie pastYearColumns, aber direkt aus dem gewählten Zeitraum-Filter statt
 * aus einer bereits eingetroffenen Server-Antwort - damit die Tabellenköpfe
 * sofort feststehen, nicht erst nach dem ersten Datenabruf.
 */
export function yearRangeExcludingCurrent(yearFrom, yearTo, currentYear) {
    const from = Number(yearFrom);
    const to = Number(yearTo);
    if (!from || !to) {
        return [];
    }
    const years = [];
    for (let year = from; year <= to; year += 1) {
        if (year !== Number(currentYear)) {
            years.push(String(year));
        }
    }
    return years;
}

/**
 * Kurzform einer Branche: die weclapp-Kennung in Klammern ("Graffiti (6)")
 * gehört nicht in schmale Tabellenspalten - der Klartext bleibt ohnehin
 * eindeutig ohne die Zahl.
 */
export function shortSector(sector) {
    if (!sector) {
        return sector;
    }
    return sector.replace(/\s*\(\d+\)\s*$/, "");
}

/**
 * Kurzform einer Preisgruppe ("PG5 - Key-Account Handel" → "PG5") für
 * schmale Tabellenspalten - der Klartext gehört daneben (klein) oder in
 * einen HelpTooltip, nie in ein title-Attribut.
 */
export function shortPriceGroup(priceGroup) {
    if (!priceGroup) {
        return priceGroup;
    }
    const trennerIndex = priceGroup.indexOf(" - ");
    return trennerIndex === -1 ? priceGroup : priceGroup.slice(0, trennerIndex);
}

export function currentYearLabel(meta) {
    if (!meta?.current_year) {
        return null;
    }
    return `${meta.current_year} bis ${monthLabel(meta.stichmonat)}`;
}

/**
 * Ermittelt aus `year_to` und dem laufenden Jahr (meta.current_year), ob das
 * Bezugsjahr R = min(year_to, current_year) bereits vollständig ist
 * (schnittstelle.md 7.2). Rein aus dem Filterzustand berechnet, damit die
 * Filterleiste (Stichmonat-Abschaltung) nicht erst auf die Antwort eines
 * einzelnen Daten-Endpunkts warten muss. Ohne bekanntes laufendes Jahr gilt
 * das bisherige Verhalten (nicht vollständig).
 */
export function isReferenceYearComplete(yearTo, currentYear) {
    if (!currentYear || !yearTo) {
        return false;
    }
    return Number(yearTo) < Number(currentYear);
}

/**
 * Normalisiert `reference.labels` einer Datenantwort auf die bisherigen,
 * festen Spaltenüberschriften als Rückfall - solange das Backend `reference`
 * noch nicht für jeden Endpunkt liefert, sieht die Oberfläche unverändert
 * aus (defensive Programmierung, schnittstelle.md 7.2). `current`/`hr` als
 * ausdrücklich übermitteltes `null` blendet die jeweilige Spalte aus (das
 * Bezugsjahr ist dann ein abgeschlossenes Jahr ohne Teiljahr/Hochrechnung).
 */
export function referenceLabels(reference, meta) {
    const labels = reference?.labels || {};
    const fallbackCurrent = currentYearLabel(meta) || "Laufendes Jahr";
    return {
        complete: Boolean(reference?.complete),
        current: "current" in labels ? labels.current : fallbackCurrent,
        hr: "hr" in labels ? labels.hr : "HR",
        plan: labels.plan ?? "Plan",
        prevVsPrevprev: labels.prev_vs_prevprev ?? "∑ vs. Vorjahr",
        hrVsPrev: labels.hr_vs_prev ?? "∑ HR vs. Vorjahr",
        hrVsPlan: labels.hr_vs_plan ?? "∑ HR vs. Plan",
        trend: labels.trend ?? "Trend",
    };
}

/**
 * Prüft ein Recht aus `meta.abilities`. Fehlt `abilities` oder das
 * einzelne Recht ganz (Backend liefert es noch nicht), gilt das bisherige
 * Verhalten - das Recht wird als vorhanden angenommen, statt die Oberfläche
 * ohne erkennbaren Grund zu sperren.
 */
export function hasAbility(meta, key) {
    const abilities = meta?.abilities;
    if (!abilities || !(key in abilities)) {
        return true;
    }
    return Boolean(abilities[key]);
}

/** Freundliche Fehlermeldung, insbesondere für 403-Antworten (fehlendes Recht). */
export function friendlyErrorMessage(e, fallback) {
    if (e?.response?.status === 403) {
        return "Für diesen Teil fehlt dir das Recht, diesen Bereich zu sehen.";
    }
    return e?.response?.data?.message || e?.message || fallback;
}

/** "Wird berechnet … (mm:ss)" - laufende Zeit im Knopf "Neu berechnen". */
export function formatElapsedClock(totalSeconds) {
    const seconds = Math.max(0, Math.floor(totalSeconds || 0));
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return `${minutes}:${String(rest).padStart(2, "0")}`;
}

/** Dauer einer abgeschlossenen Neuberechnung in Worten ("2 Min. 34 Sek."). */
export function formatDurationWords(totalSeconds) {
    if (totalSeconds === null || totalSeconds === undefined || Number.isNaN(Number(totalSeconds))) {
        return null;
    }
    const seconds = Math.max(0, Math.round(totalSeconds));
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return minutes > 0 ? `${minutes} Min. ${rest} Sek.` : `${rest} Sek.`;
}

/**
 * Kurzfassung der gewählten Filter für die Handy-Zeile des fixierten
 * Filterblocks (StickyFilterBlock), z. B. „2023–2026 · Stichmonat 8 ·
 * Graffiti, Fine Arts · SH“. `extra` hängt seitenspezifische Angaben an
 * (Monate, Vertreter der Seite „Vertreter“, Farbton).
 */
export function filterZusammenfassung(filters, extra = []) {
    const teile = [];
    const {meta, yearFrom, yearTo, stichmonat, referenceComplete, sectors, reps, articleTypes, productLines, withoutFreight} = filters;
    if (yearFrom && yearTo) {
        teile.push(yearFrom === yearTo ? String(yearFrom) : `${yearFrom}–${yearTo}`);
    }
    if (stichmonat && !referenceComplete) {
        teile.push(`Stichmonat ${stichmonat}`);
    }
    const namenAus = (liste, werte, feldWert, feldName) => {
        const namen = werte.map((wert) => (liste || []).find((eintrag) => String(eintrag[feldWert]) === String(wert))?.[feldName] ?? wert);
        return namen.length > 2 ? `${namen.slice(0, 2).join(", ")} +${namen.length - 2}` : namen.join(", ");
    };
    if (sectors?.length) {
        teile.push(namenAus(meta?.sectors, sectors.filter((s) => s !== "none"), "id", "name") + (sectors.includes("none") ? " + ohne Branche" : ""));
    }
    if (reps?.length) {
        teile.push(reps.map((r) => (r === "pool" ? "Nicht zugeordnet" : r)).join(", "));
    }
    if (articleTypes?.length) {
        teile.push(namenAus(null, articleTypes));
    }
    if (productLines?.length) {
        teile.push(namenAus(null, productLines));
    }
    if (filters.articles?.length) {
        teile.push(filters.articles.length === 1 ? `Artikel ${filters.articles[0]}` : `${filters.articles.length} Artikel`);
    }
    if (withoutFreight) {
        teile.push("ohne Fracht");
    }
    return [...teile, ...extra.filter(Boolean)].join(" · ");
}
